import {
  AppBar,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FacebookIcon from "@mui/icons-material/Facebook";
import FilterListIcon from "@mui/icons-material/FilterList";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import InstagramIcon from "@mui/icons-material/Instagram";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import PauseIcon from "@mui/icons-material/Pause";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TwitterIcon from "@mui/icons-material/Twitter";
import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";

import cinchLogo from "../../assets/cinch-logo.svg";
import brandJiffyLube from "../../assets/brand-jiffy-lube.svg";
import brandThorn from "../../assets/brand-thorn.svg";
import brandOrkin from "../../assets/brand-orkin.svg";
import brandGreaseMonkey from "../../assets/brand-grease-monkey.svg";
import brandMoxie from "../../assets/brand-moxie.svg";
import wizardStep1 from "../../assets/va-wizard-step1.png";
import wizardStep2 from "../../assets/va-wizard-step2.png";
import wizardStep3 from "../../assets/va-wizard-step3.png";
import wizardStep4 from "../../assets/va-wizard-step4.png";
import wizardStep5 from "../../assets/va-wizard-step5.png";
import { getSignupStatus, submitSignup } from "../../api";
import useRecaptcha from "../../hooks/use-recaptcha";
import posthog, { getUtmPayload, isInternalEmail } from "../../posthog";
import { RECAPTCHA_SITE_KEY } from "../../recaptcha-key";
import { NAICS_OTHER } from "./naics-options";
import SignupSuccessScreen from "./signup-success-screen";
import VoiceAgentSignupForm from "./voice-agent-signup-form";

const FEATURE_FOCUS = "voice-agent";
const SIGN_IN_URL = "https://marketing.cinch.io/sign-in";
const DEMO_PHONE_NUMBER = "(762) 910-3772";

const INITIAL_FORM = {
  first_name: "",
  last_name: "",
  email: "",
  company_name: "",
  naics_choice: "",
  naics_other: "",
  terms_accepted: false,
};

const REQUIRED_FIELDS = ["first_name", "last_name", "email", "company_name"];

const HERO_HEADLINE = "AI-Powered Call Assistant for B2C Businesses";
const HERO_SUBHEAD =
  "AI Voice answers every call 24/7, so customers get the help they need even when you're closed.";
const HERO_BULLETS = [
  "No more missed calls",
  "Set it up in minutes",
  "Works across all your locations",
  "Natural conversations",
];
// Logo-strip names kept as text placeholders — real brand marks ship with
// the marketing-asset bundle Erica/Emii will drop in a follow-up.
// Each logo's natural SVG aspect differs, so sizing by height alone makes
// wide wordmarks (Jiffy, Thorn, Moxie) render much wider than the squarer
// badge marks (Orkin, Grease Monkey). We size each by both a target width
// and height picked to land near equal visual weight in the hero bar.
const CUSTOMER_LOGOS = [
  { name: "Jiffy Lube", src: brandJiffyLube, w: 102, h: 24 },
  { name: "Thorn Pest Control", src: brandThorn, w: 86, h: 20 },
  { name: "Orkin", src: brandOrkin, w: 60, h: 32 },
  { name: "Grease Monkey", src: brandGreaseMonkey, w: 70, h: 28 },
  { name: "Moxie", src: brandMoxie, w: 92, h: 20 },
];

// Marketing-CDN demo clips. Same URLs the hero-CTA player on frontend-st
// uses (voice-agent-cta-self-serve). Only two clips exist today — the
// Diagnostic card reuses one as a placeholder until a dedicated sample
// is produced; swap `src` when it lands.
const AUDIO_DEMOS = [
  {
    key: "service",
    title: "Service Inquiry",
    src: "https://marketing.cinch.io/assets/aud/ai-voice-oil-change.mp3",
    grad: "linear-gradient(135deg,#0994F2,#A3E635)",
  },
  {
    key: "coupon",
    title: "Coupon Request",
    src: "https://marketing.cinch.io/assets/aud/ai-voice-coupon.mp3",
    grad: "linear-gradient(135deg,#FF7959,#FFE1C7)",
  },
];

const DASHBOARD_FEATURES = [
  { title: "Answers Calls 24/7", body: "AI Voice answers every call, day or night." },
  { title: "Captures Details", body: "AI Voice captures every detail from every call." },
  { title: "Schedules Callbacks", body: "After hours? Callbacks still get scheduled live." },
];

const SETUP_STEPS = [
  {
    title: "Add your business info",
    detail:
      "Turn on website scanning so the AI learns your services, hours, and FAQs automatically. Review or update anything at any time.",
    preview: wizardStep1,
  },
  {
    title: "Pick a voice and tone",
    detail:
      "Choose a greeting, personality, and voice. Pick from professional, friendly, or efficient — your agent speaks every language your callers do.",
    preview: wizardStep2,
  },
  {
    title: "Choose what your assistant handles",
    detail:
      "Turn on the behaviors that fit your business: callbacks, warm transfers, custom business info, website lookups. Hours and address are on by default.",
    preview: wizardStep3,
  },
  {
    title: "Connect your phone number",
    detail:
      "Forward your business line to your agent with a code from your carrier, or point your VoIP system at the agent's number — most setups take under five minutes.",
    preview: wizardStep4,
  },
  {
    title: "Go live",
    detail:
      "Review every setting, test a call from your browser, then flip the switch — your agent starts answering real customers.",
    preview: wizardStep5,
  },
];

// Pricing kept in lockstep with frontend-st's PLANS
// (billing/self-serve/checkout/voice-agent-checkout/voice-agent-checkout-shared.js).
// Taglines use Erica's Figma copy; minute allowances and overage rate are
// product-side data. If frontend-st's PLANS change, update here too — these
// two sources of truth drift until there's a shared package.
const PRICING_TIERS = [
  {
    name: "Starter",
    price: "$99",
    unit: "/ month / location",
    minutes: 250,
    tagline: "Perfect for small businesses getting started.",
    highlighted: false,
  },
  {
    name: "Standard",
    price: "$199",
    unit: "/ month / location",
    minutes: 500,
    tagline: "Best value for growing businesses.",
    highlighted: true,
  },
  {
    name: "Scale",
    price: "$275",
    unit: "/ month / location",
    minutes: 700,
    tagline: "For large organizations.",
    highlighted: false,
  },
];

const WHAT_YOU_GET = {
  collectsTitle: "The voice agent collects:",
  collects: [
    "Caller name and phone number",
    "What they're calling about",
    "Best time to reach them",
    "A full conversation summary",
  ],
  controlTitle: "You stay in control:",
  control: [
    "Get notified when calls come in",
    "Transfer calls to your team when needed",
    "Review call recordings and transcripts",
    "Every call becomes a customer record",
  ],
};

const FAQ_ITEMS = [
  {
    q: "What happens after my 25 free minutes?",
    a: "You keep access to everything you built — your agent, your settings, your call history. To keep taking calls, pick a plan below. No surprise charges; we won't start billing until you choose a plan yourself.",
  },
  {
    q: "Will this sound robotic?",
    a: "No. It sounds like a real person on the other end — natural phrasing, pauses, and tone. Most callers don't realize they're talking to an AI until you tell them.",
  },
  {
    q: "What if it can't answer a question?",
    a: "The agent is honest when it doesn't know. It offers to take a message, book a callback, or hand off to a number you specify — whichever you've configured for that scenario.",
  },
  {
    q: "Does this replace my team?",
    a: "No — it covers the calls your team can't get to (after-hours, lunch, everyone-on-the-phone-already) and the repetitive questions they'd rather not spend their day on. Your team keeps the conversations that need a human.",
  },
  {
    q: "Is setup complicated?",
    a: "It takes about 10 minutes. Walk through five short steps (business info, voice, scenarios it handles, phone number, go-live) and you're live.",
  },
  {
    q: "Does this actually help my business make more money?",
    a: "Every missed call is a customer who called a competitor instead. We've seen customers recover 20–40% of their missed-call volume within the first month of going live.",
  },
];

// Decorative blue wave used behind the hero and the final CTA. Mirrors
// vertically on the final-CTA block.
function HeroWave({ flipY = false, opacity = 1 }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: flipY ? "auto" : 0,
        top: flipY ? 0 : "auto",
        height: { xs: 90, md: 160 },
        pointerEvents: "none",
        opacity,
        transform: flipY ? "scaleY(-1)" : "none",
      }}
    >
      <svg
        viewBox="0 0 1440 160"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="hw-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#AED5F3" />
            <stop offset="100%" stopColor="#CFE4F6" />
          </linearGradient>
        </defs>
        <path
          d="M0,80 C320,160 640,0 960,80 C1200,140 1320,120 1440,70 L1440,160 L0,160 Z"
          fill="url(#hw-grad)"
          opacity="0.9"
        />
        <path
          d="M0,110 C300,180 720,30 1080,100 C1260,135 1360,125 1440,105 L1440,160 L0,160 Z"
          fill="#BAD9EF"
          opacity="0.55"
        />
      </svg>
    </Box>
  );
}

export default function VoiceAgentSignup() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [signupEnabled, setSignupEnabled] = useState(null);
  const [signupDisabledMessage, setSignupDisabledMessage] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(FAQ_ITEMS[0].q);
  const [expandedSetupStep, setExpandedSetupStep] = useState(0);

  const formStartedRef = useRef(false);
  const activeSubmitSourceRef = useRef(null);

  useEffect(() => {
    posthog.capture("signup_page_viewed", { feature_focus: FEATURE_FOCUS });
    const previousTitle = document.title;
    document.title = "Cinch AI Voice Agent — Try It Free";
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSignupStatus(FEATURE_FOCUS)
      .then((response) => {
        if (cancelled) return;
        setSignupEnabled(Boolean(response.data?.signup_enabled));
        setSignupDisabledMessage(response.data?.message || null);
      })
      .catch(() => {
        if (cancelled) return;
        setSignupEnabled(true);
        setSignupDisabledMessage(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { recaptchaRef, handleRecaptcha, setCaptchaToken } = useRecaptcha();
  const theme = useTheme();
  const fullScreenCaptcha = useMediaQuery(theme.breakpoints.down("sm"));

  if (result) {
    const handleStartOver = () => {
      setResult(null);
      setCaptchaToken(null);
      setCaptchaOpen(false);
      setError(null);
      setFieldErrors({});
      setForm(INITIAL_FORM);
      if (recaptchaRef.current) recaptchaRef.current.reset();
    };
    return (
      <SignupSuccessScreen
        email={form.email.trim()}
        onStartOver={handleStartOver}
        featureFocus={FEATURE_FOCUS}
      />
    );
  }

  const update = (field) => (e) => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      posthog.capture("signup_form_started", { feature_focus: FEATURE_FOCUS });
    }
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const naicsIsOther = form.naics_choice === NAICS_OTHER;

  const validateClientSide = () => {
    const errs = {};
    REQUIRED_FIELDS.forEach((f) => {
      if (!form[f].trim()) errs[f] = ["This field is required."];
    });
    if (!form.naics_choice) {
      errs.naics = ["Please pick an industry."];
    } else if (naicsIsOther && !form.naics_other.trim()) {
      errs.naics = ["Please tell us about your industry."];
    }
    if (!form.terms_accepted) {
      errs.terms_accepted = [
        "You must agree to the Terms of Service to continue.",
      ];
    }
    return errs;
  };

  const makeSubmitHandler = (source) => (e) => {
    e.preventDefault();
    activeSubmitSourceRef.current = source;
    setError(null);
    const errs = validateClientSide();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please correct the highlighted fields and try again.");
      posthog.capture("signup_validation_failed", {
        feature_focus: FEATURE_FOCUS,
        fields: Object.keys(errs),
        form_instance: source,
      });
      if (source === "pricing") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    setFieldErrors({});
    posthog.register({ is_internal_user: isInternalEmail(form.email) });
    setCaptchaOpen(true);
    posthog.capture("signup_captcha_shown", {
      feature_focus: FEATURE_FOCUS,
      form_instance: source,
    });
  };

  const handleCancelCaptcha = () => {
    setCaptchaOpen(false);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    setCaptchaToken("");
  };

  const onCaptchaChange = async (token) => {
    handleRecaptcha(token);
    if (!token) return;
    posthog.capture("signup_captcha_solved", { feature_focus: FEATURE_FOCUS });

    const naics = naicsIsOther ? form.naics_other.trim() : form.naics_choice;
    const email = form.email.trim();
    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      email,
      company_name: form.company_name.trim(),
      naics,
      terms_accepted: form.terms_accepted,
      ...getUtmPayload(),
      captcha: token,
    };

    setSubmitting(true);
    try {
      const response = await submitSignup(FEATURE_FOCUS, payload);
      const status = response.data?.status || "success";
      posthog.capture("signup_completed", {
        feature_focus: FEATURE_FOCUS,
        status,
        form_instance: activeSubmitSourceRef.current,
      });
      if (status === "success") {
        posthog.identify(email.toLowerCase(), {
          email,
          name: `${payload.first_name} ${payload.last_name}`.trim(),
          company_name: payload.company_name,
          signup_feature_focus: FEATURE_FOCUS,
          is_internal_user: isInternalEmail(email),
        });
      }
      setResult(response.data);
      setCaptchaOpen(false);
    } catch (err) {
      const httpStatus = err.response?.status;
      let errorKind = "unknown";
      if (httpStatus === 400 && err.response.data?.errors) {
        errorKind = "validation";
        setFieldErrors(err.response.data.errors);
        setError("Please correct the highlighted fields and try again.");
      } else if (httpStatus === 429) {
        errorKind = "rate_limited";
        setError(
          "We've received a lot of requests from your network. Please try again in a bit.",
        );
      } else if (httpStatus === 503) {
        errorKind = "kill_switch";
        setError(
          err.response.data?.message ||
            "Signups are temporarily unavailable. Please try again later or contact support.",
        );
      } else {
        errorKind = "server_error";
        setError(
          "Something went wrong on our end. Please try again in a moment.",
        );
      }
      posthog.capture("signup_failed", {
        feature_focus: FEATURE_FOCUS,
        http_status: httpStatus || null,
        error_kind: errorKind,
      });
      setCaptchaOpen(false);
      if (recaptchaRef.current) recaptchaRef.current.reset();
      setCaptchaToken("");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignInLinkClick = () => {
    posthog.capture("signup_existing_account_link_clicked", {
      feature_focus: FEATURE_FOCUS,
    });
  };

  const scrollToHeroForm = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sharedFormProps = {
    form,
    fieldErrors,
    error,
    signupEnabled,
    signupDisabledMessage,
    submitting,
    onChange: update,
    onSignInClick: handleSignInLinkClick,
  };

  const demoHref = `tel:${DEMO_PHONE_NUMBER.replace(/\D/g, "")}`;

  return (
    <Box sx={{ bgcolor: "#FFFFFF", minHeight: "100vh" }}>
      {/* NAV */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: "1px solid #E4E7EC",
          color: "#111827",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1, minHeight: { xs: 64, md: 72 } }}>
            <Box
              role="img"
              aria-label="Cinch"
              sx={{
                width: 90,
                height: 32,
                bgcolor: "#0875BF",
                mask: `url(${cinchLogo}) no-repeat center / contain`,
                WebkitMask: `url(${cinchLogo}) no-repeat center / contain`,
              }}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center">
              <Link
                href={SIGN_IN_URL}
                underline="hover"
                onClick={handleSignInLinkClick}
                sx={{
                  color: "#111827",
                  fontWeight: 500,
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  display: { xs: "none", sm: "inline" },
                }}
              >
                Sign in
              </Link>
              <Button
                variant="contained"
                size="small"
                onClick={scrollToHeroForm}
                sx={{
                  bgcolor: "#0875BF",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  boxShadow: "none",
                  px: 2,
                  "&:hover": { bgcolor: "#076AA8", boxShadow: "none" },
                }}
              >
                Start Free Trial
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      {/* HERO */}
      <Box
        sx={{
          position: "relative",
          bgcolor: "#FFFFFF",
          pt: { xs: 4, md: 8 },
          pb: { xs: 10, md: 14 },
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
              <Stack spacing={{ xs: 2, md: 3 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem" },
                    lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    color: "#111827",
                  }}
                >
                  {HERO_HEADLINE}
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(0,0,0,0.7)",
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    lineHeight: 1.5,
                    maxWidth: 560,
                  }}
                >
                  {HERO_SUBHEAD}
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 1 }}>
                  {HERO_BULLETS.map((b) => (
                    <Stack
                      key={b}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <CheckCircleIcon sx={{ color: "#0875BF", fontSize: 20 }} />
                      <Typography
                        sx={{
                          color: "#111827",
                          fontWeight: 600,
                          fontSize: { xs: "0.95rem", md: "1rem" },
                        }}
                      >
                        {b}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>

                <Stack
                  direction="row"
                  spacing={{ xs: 2.5, sm: 4 }}
                  alignItems="center"
                  sx={{
                    pt: { xs: 2, md: 4 },
                    flexWrap: "wrap",
                    rowGap: 2,
                  }}
                >
                  {CUSTOMER_LOGOS.map((logo) => (
                    <Box
                      key={logo.name}
                      component="img"
                      src={logo.src}
                      alt={logo.name}
                      sx={{
                        maxWidth: logo.w,
                        maxHeight: logo.h,
                        height: "auto",
                        width: "auto",
                        display: "block",
                        objectFit: "contain",
                        opacity: 0.72,
                      }}
                    />
                  ))}
                </Stack>
              </Stack>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              order={{ xs: 1, md: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                <VoiceAgentSignupForm
                  {...sharedFormProps}
                  onSubmit={makeSubmitHandler("hero")}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
        <HeroWave />
      </Box>

      {/* AUDIO DEMOS */}
      <SectionWrapper bg="#FAFAFA">
        <SectionHeader
          eyebrow="Audio Examples"
          eyebrowIcon={<GraphicEqIcon sx={{ fontSize: 16 }} />}
          title="Hear What Your Customers Hear"
        />
        <Stack spacing={1.5} sx={{ textAlign: "center", mb: 4, maxWidth: 760, mx: "auto" }}>
          <Typography sx={{ color: "rgba(0,0,0,0.7)", fontSize: { xs: "0.95rem", md: "1rem" }, lineHeight: 1.6 }}>
            Most automated phone systems make people press button after button
            until they hang up frustrated. Cinch AI Voice just answers the
            phone and helps.
          </Typography>
          <Typography sx={{ color: "rgba(0,0,0,0.7)", fontSize: { xs: "0.95rem", md: "1rem" }, lineHeight: 1.6 }}>
            Call the demo number or listen to a few real examples.
          </Typography>
        </Stack>

        <Stack alignItems="center" sx={{ mb: 5 }}>
          <Button
            variant="contained"
            size="large"
            href={demoHref}
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: "#191919",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              px: 3,
              py: 1.25,
              boxShadow: "none",
              "&:hover": { bgcolor: "#2A2A2A", boxShadow: "none" },
            }}
          >
            Call {DEMO_PHONE_NUMBER}
          </Button>
        </Stack>

        <Grid container spacing={3} justifyContent="center">
          {AUDIO_DEMOS.map((d) => (
            <Grid item xs={12} sm={6} key={d.key}>
              <AudioDemoCard clip={d} />
            </Grid>
          ))}
        </Grid>
      </SectionWrapper>

      {/* DASHBOARD / BE THE SHOP CUSTOMERS CHOOSE FIRST */}
      <SectionWrapper bg="#FFFFFF">
        <SectionHeader
          eyebrow="Features"
          eyebrowIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          title="Be the Shop Customers Choose First"
        />
        <Stack spacing={1.5} sx={{ textAlign: "center", mb: 5, maxWidth: 760, mx: "auto" }}>
          <Typography sx={{ color: "rgba(0,0,0,0.7)", fontSize: { xs: "0.95rem", md: "1rem" }, lineHeight: 1.6 }}>
            If no one picks up, callers don't wait. They call the next
            business. People want help now. When no one answers, that revenue
            goes somewhere else.
          </Typography>
          <Typography sx={{ color: "rgba(0,0,0,0.7)", fontSize: { xs: "0.95rem", md: "1rem" }, lineHeight: 1.6 }}>
            Cinch AI Voice makes sure every call has a real chance to turn
            into business.
          </Typography>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: "#E4E7EC",
            overflow: "hidden",
            bgcolor: "#FAFAFA",
          }}
        >
          <Grid container spacing={0} sx={{ p: { xs: 2.5, md: 4 } }}>
            {DASHBOARD_FEATURES.map((f, i) => (
              <Grid item xs={12} md={4} key={f.title} sx={{ mb: { xs: 2, md: 0 } }}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="flex-start"
                  sx={{ pr: { md: 3 } }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "#EFF6FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mt: 0.25,
                    }}
                  >
                    {i === 0 && <PhoneInTalkIcon sx={{ fontSize: 15, color: "#0875BF" }} />}
                    {i === 1 && <AutoAwesomeIcon sx={{ fontSize: 15, color: "#0875BF" }} />}
                    {i === 2 && <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "#0875BF" }} />}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.95rem" }}>
                      {f.title}
                    </Typography>
                    <Typography sx={{ color: "rgba(0,0,0,0.6)", fontSize: "0.875rem", mt: 0.25 }}>
                      {f.body}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ p: { xs: 1.5, md: 2 }, pt: 0 }}>
            <DashboardMock />
          </Box>
        </Paper>
      </SectionWrapper>

      {/* SETUP WIZARD */}
      <SectionWrapper bg="#FAFAFA">
        <SectionHeader
          eyebrow="How It Works"
          eyebrowIcon={<NorthEastIcon sx={{ fontSize: 16 }} />}
          title="Set Up Your AI Call Assistant in Minutes"
        />
        <Typography
          sx={{
            textAlign: "center",
            maxWidth: 760,
            mx: "auto",
            mb: 5,
            color: "rgba(0,0,0,0.7)",
            fontSize: { xs: "0.95rem", md: "1rem" },
            lineHeight: 1.6,
          }}
        >
          Getting started takes just a few minutes. Tell us about your
          business, customize your greeting, and connect your phone line.
          Your AI assistant handles the rest.
        </Typography>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">
          <Grid item xs={12} md={6}>
            <WizardStepPreview
              src={SETUP_STEPS[Math.max(0, expandedSetupStep)]?.preview || SETUP_STEPS[0].preview}
              label={SETUP_STEPS[Math.max(0, expandedSetupStep)]?.title || SETUP_STEPS[0].title}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={1.5}>
              {SETUP_STEPS.map((s, i) => {
                const isExpanded = expandedSetupStep === i;
                return (
                  <Paper
                    key={s.title}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: isExpanded ? "#0875BF" : "#E4E7EC",
                      borderLeftWidth: isExpanded ? 3 : 1,
                      bgcolor: "#FFFFFF",
                      cursor: "pointer",
                      transition: "border-color 0.15s ease",
                      "&:hover": { borderColor: isExpanded ? "#0875BF" : "#CBD5E1" },
                    }}
                    onClick={() => setExpandedSetupStep(isExpanded ? -1 : i)}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#111827" }}>
                      {i + 1}. {s.title}
                    </Typography>
                    {isExpanded && s.detail && (
                      <Typography sx={{ color: "rgba(0,0,0,0.65)", mt: 1, fontSize: "0.9rem", lineHeight: 1.55 }}>
                        {s.detail}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          </Grid>
        </Grid>
      </SectionWrapper>

      {/* DARK NAVY — WHAT YOU GET */}
      <SectionWrapper bg="#05092C" dark>
        <SectionHeader
          eyebrow="What You Get"
          eyebrowIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
          title="Help Your Customers and Protect Your Revenue"
          dark
        />
        <Typography
          sx={{
            textAlign: "center",
            maxWidth: 820,
            mx: "auto",
            mb: 5,
            color: "rgba(255,255,255,0.78)",
            fontSize: { xs: "0.95rem", md: "1rem" },
            lineHeight: 1.6,
          }}
        >
          When calls are answered, customers stay engaged. When details are
          captured, follow-ups actually happen. That's how busy days turn into
          booked work instead of missed chances.
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, bgcolor: "#FAFAFA", color: "#111827" }}>
              <CallTopicsChart />
              <Typography sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
                {WHAT_YOU_GET.collectsTitle}
              </Typography>
              <Stack spacing={1}>
                {WHAT_YOU_GET.collects.map((item) => (
                  <Stack key={item} direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#0875BF" }} />
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.75)" }}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 2, bgcolor: "#FAFAFA", color: "#111827" }}>
              <UsageTrendChart />
              <Typography sx={{ fontWeight: 700, mt: 3, mb: 1.5 }}>
                {WHAT_YOU_GET.controlTitle}
              </Typography>
              <Stack spacing={1}>
                {WHAT_YOU_GET.control.map((item) => (
                  <Stack key={item} direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "#0875BF" }} />
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.75)" }}>
                      {item}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </SectionWrapper>

      {/* PRICING + SECOND FORM INSTANCE */}
      <SectionWrapper bg="#FAFAFA" id="pricing">
        <SectionHeader
          eyebrow="Pricing"
          title="Try Free. Pay When You're Ready."
        />
        <Typography
          sx={{
            textAlign: "center",
            maxWidth: 760,
            mx: "auto",
            mb: 5,
            color: "rgba(0,0,0,0.7)",
            fontSize: { xs: "0.95rem", md: "1rem" },
            lineHeight: 1.6,
          }}
        >
          Build and test your AI call assistant at no cost. When you're ready
          to use it with real customers, pricing scales with call volume. No
          contracts. Upgrade or cancel anytime.
        </Typography>
        <Grid container spacing={3} alignItems="stretch">
          {PRICING_TIERS.map((tier) => (
            <Grid item xs={12} md={4} key={tier.name}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 2,
                  borderColor: tier.highlighted ? "#0875BF" : "#E4E7EC",
                  borderWidth: tier.highlighted ? 2 : 1,
                  bgcolor: "#FFFFFF",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  position: "relative",
                  boxShadow: tier.highlighted
                    ? "0 8px 24px rgba(8,117,191,0.12)"
                    : "none",
                }}
              >
                {tier.highlighted && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      bgcolor: "#0875BF",
                      color: "#FFFFFF",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 999,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                    }}
                  >
                    Most Popular
                  </Box>
                )}
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#111827" }}>
                  {tier.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mt: 0.5, minHeight: 44 }}>
                  {tier.tagline}
                </Typography>
                <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 3 }}>
                  <Typography sx={{ fontSize: { xs: "2.25rem", md: "2.75rem" }, fontWeight: 700, color: "#111827", lineHeight: 1 }}>
                    {tier.price}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
                    {tier.unit}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#0875BF" }} />
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    {tier.minutes.toLocaleString()} minutes included
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "#0875BF" }} />
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    No credit card required for signup
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Stack alignItems="center" sx={{ mt: 5 }}>
          <Button
            variant="contained"
            size="large"
            onClick={scrollToHeroForm}
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: "#0875BF",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              px: 4,
              py: 1.25,
              borderRadius: 1.5,
              boxShadow: "none",
              "&:hover": { bgcolor: "#076AA8", boxShadow: "none" },
            }}
          >
            Start Your Free Trial
          </Button>
        </Stack>
      </SectionWrapper>

      {/* FAQ */}
      <SectionWrapper bg="#FFFFFF">
        <SectionHeader
          eyebrow="Frequently Asked Questions"
          eyebrowIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
          title="Got Questions?"
        />
        <Box sx={{ maxWidth: 820, mx: "auto" }}>
          {FAQ_ITEMS.map((item) => (
            <Accordion
              key={item.q}
              expanded={expandedFaq === item.q}
              onChange={(_, isExpanded) =>
                setExpandedFaq(isExpanded ? item.q : null)
              }
              elevation={0}
              sx={{
                borderBottom: "1px solid #E4E7EC",
                "&:before": { display: "none" },
                bgcolor: "transparent",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600, color: "#111827" }}>
                  {item.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: "rgba(0,0,0,0.7)", lineHeight: 1.6 }}>
                  {item.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </SectionWrapper>

      {/* FINAL CTA */}
      <Box sx={{ position: "relative", bgcolor: "#FFFFFF", pt: { xs: 14, md: 20 }, pb: { xs: 8, md: 12 }, overflow: "hidden" }}>
        <HeroWave flipY opacity={0.9} />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <SectionHeader
            eyebrow="Try It free"
            eyebrowIcon={<PhoneInTalkIcon sx={{ fontSize: 16 }} />}
            title="Still Skeptical? Call It."
          />
          <Typography
            sx={{
              textAlign: "center",
              maxWidth: 680,
              mx: "auto",
              mb: 4,
              color: "rgba(0,0,0,0.7)",
              fontSize: { xs: "0.95rem", md: "1rem" },
              lineHeight: 1.6,
            }}
          >
            The best way to know if AI Voice is right for your business is to
            experience it. Ask about hours, services, or callbacks.
          </Typography>
          <Stack alignItems="center">
            <Button
              variant="contained"
              size="large"
              href={demoHref}
              endIcon={<ArrowForwardIcon />}
              sx={{
                bgcolor: "#191919",
                color: "#FFFFFF",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "10px",
                px: 4,
                py: 1.5,
                boxShadow: "none",
                "&:hover": { bgcolor: "#2A2A2A", boxShadow: "none" },
              }}
            >
              Call {DEMO_PHONE_NUMBER}
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box sx={{ bgcolor: "#05092C", color: "#FFFFFF", py: 4 }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              role="img"
              aria-label="Cinch"
              sx={{
                width: 90,
                height: 32,
                bgcolor: "#FFFFFF",
                mask: `url(${cinchLogo}) no-repeat center / contain`,
                WebkitMask: `url(${cinchLogo}) no-repeat center / contain`,
              }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                size="small"
                aria-label="Twitter"
                href="https://twitter.com/cinchplatform"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#FFFFFF" }}
              >
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Facebook"
                href="https://facebook.com/cinchplatform"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#FFFFFF" }}
              >
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label="Instagram"
                href="https://instagram.com/cinchplatform"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#FFFFFF" }}
              >
                <InstagramIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.5)", mt: 2, display: "block", textAlign: { xs: "center", sm: "left" } }}
          >
            © Copyright {new Date().getFullYear()}, All Rights Reserved
          </Typography>
        </Container>
      </Box>

      {/* CAPTCHA MODAL */}
      <Dialog
        open={captchaOpen}
        onClose={submitting ? undefined : handleCancelCaptcha}
        aria-labelledby="captcha-dialog-title"
        fullScreen={fullScreenCaptcha}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: 4 },
            px: { xs: 0, sm: 2 },
            py: 1,
            minWidth: { xs: "auto", sm: 380 },
          },
        }}
      >
        <DialogTitle
          id="captcha-dialog-title"
          sx={{ pb: 0.5, fontWeight: 600, px: { xs: 2, sm: 3 } }}
        >
          One quick check
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: { xs: 0.5, sm: 3 } }}>
          <DialogContentText sx={{ mb: 2.5, px: { xs: 1.5, sm: 0 } }}>
            Confirm you're human and we'll create your account.
          </DialogContentText>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              minHeight: 90,
              alignItems: "center",
            }}
          >
            {submitting ? (
              <Stack direction="row" spacing={2} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Creating your account…</Typography>
              </Stack>
            ) : (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={onCaptchaChange}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            onClick={handleCancelCaptcha}
            disabled={submitting}
            variant="contained"
            color="inherit"
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 3,
              bgcolor: "#E4E8EE",
              color: "#1D252D",
              boxShadow: "none",
              "&:hover": { bgcolor: "#D5DAE2", boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Local section helpers ---

function SectionWrapper({ children, bg = "#FFFFFF", dark = false, id }) {
  return (
    <Box id={id} sx={{ bgcolor: bg, py: { xs: 6, md: 10 }, color: dark ? "#FFFFFF" : "#111827" }}>
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
}

function SectionHeader({ eyebrow, eyebrowIcon, title, dark = false }) {
  return (
    <Stack spacing={1.5} alignItems="center" sx={{ mb: { xs: 3, md: 4 } }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: dark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.6)" }}>
        {eyebrowIcon}
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.75rem",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "inherit",
          }}
        >
          {eyebrow}
        </Typography>
      </Stack>
      <Typography
        component="h2"
        sx={{
          fontWeight: 700,
          fontSize: { xs: "2rem", md: "3rem" },
          textAlign: "center",
          color: dark ? "#FFFFFF" : "#111827",
          lineHeight: 1.15,
          letterSpacing: "-0.01em",
          maxWidth: 920,
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}

// Real-audio demo card used in the "Hear What Your Customers Hear" row.
// Drives playback via a hidden <audio> element so the play/pause button
// and progress bar reflect actual position in the clip. Pattern mirrors
// the hero CTA on frontend-st (voice-agent-cta-self-serve.component.js).
function AudioDemoCard({ clip }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      // Autoplay restrictions and missing-file errors fall silent — the
      // UI stays in the paused state so there's no "stuck playing" lie.
      audio.play().catch(() => setIsPlaying(false));
    } else {
      audio.pause();
    }
  };

  const progressPct =
    duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        textAlign: "center",
        borderColor: "#E0E0E0",
        bgcolor: "#F5F5F5",
      }}
    >
      <audio
        ref={audioRef}
        src={clip.src}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
      />
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: clip.grad,
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          mx: "auto",
          mb: 2,
        }}
      />
      <Typography sx={{ fontWeight: 700, mb: 2, color: "#111827" }}>
        {clip.title}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IconButton
          size="small"
          onClick={togglePlay}
          aria-label={isPlaying ? `Pause ${clip.title}` : `Play ${clip.title}`}
          sx={{ bgcolor: "#F3F4F6", "&:hover": { bgcolor: "#E5E7EB" } }}
        >
          {isPlaying ? (
            <PauseIcon sx={{ color: "#111827" }} />
          ) : (
            <PlayArrowIcon sx={{ color: "#111827" }} />
          )}
        </IconButton>
        <Typography variant="caption" sx={{ color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>
          {formatClock(currentTime)}
        </Typography>
        <Box
          sx={{
            flex: 1,
            height: 4,
            bgcolor: "#E5E7EB",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: `${progressPct}%`,
              background: clip.grad,
              transition: "width 0.1s linear",
            }}
          />
        </Box>
        <Typography variant="caption" sx={{ color: "#6B7280", fontVariantNumeric: "tabular-nums" }}>
          {formatClock(duration)}
        </Typography>
      </Stack>
    </Paper>
  );
}

function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// DOM-only replica of the AI Voice Agents admin surface used as the
// hero image in the "Be the Shop Customers Choose First" section.
// Numbers are static illustrations, not live data. Keep the vibe close
// to the real product UI so visitors recognize what they're buying.
function DashboardMock() {
  const stats = [
    { label: "Total Agents", icon: <PhoneIcon sx={{ fontSize: 16, color: "#6B7280" }} />, value: "3", sub: "2 active" },
    { label: "Total Calls", icon: <TrendingUpIcon sx={{ fontSize: 16, color: "#6B7280" }} />, value: "2,140", sub: "95.8% answer rate", subColor: "#059669" },
    { label: "Avg Transfer Rate", icon: <SwapHorizIcon sx={{ fontSize: 16, color: "#6B7280" }} />, value: "20.0%", sub: "Calls to team" },
    { label: "Satisfaction", icon: <SentimentSatisfiedAltIcon sx={{ fontSize: 16, color: "#6B7280" }} />, value: "4.5/5.0", sub: "Average rating" },
  ];
  const rowCols = [
    { label: "Total Calls", value: "1,248" },
    { label: "Answer Rate", value: "95.0%" },
    { label: "Avg Duration", value: "2:22" },
    { label: "Transfer Rate", value: "18%" },
    { label: "Satisfaction", value: "4.6/5.0" },
  ];
  return (
    <Box
      sx={{
        bgcolor: "#1E293B",
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        pt: { xs: 1.5, md: 2 },
        px: { xs: 1.5, md: 2 },
        pb: 0,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          bgcolor: "#FFFFFF",
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          p: { xs: 2, md: 3 },
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} sx={{ mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "1rem" }}>
              AI Voice Agents
            </Typography>
            <Typography variant="caption" sx={{ color: "#6B7280" }}>
              Manage and monitor your AI-powered phone agents
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            sx={{
              bgcolor: "#6366F1",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1,
              boxShadow: "none",
              "&:hover": { bgcolor: "#4F46E5", boxShadow: "none" },
            }}
          >
            Create Agent
          </Button>
        </Stack>

        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {stats.map((s) => (
            <Grid item xs={6} md={3} key={s.label}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.25, borderColor: "#E5E7EB" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: "0.75rem", color: "#6B7280" }}>{s.label}</Typography>
                  {s.icon}
                </Stack>
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color: "#111827", mt: 0.5 }}>
                  {s.value}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: s.subColor || "#6B7280" }}>
                  {s.sub}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 1,
              borderColor: "#E5E7EB",
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <SearchIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
            <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
              Search by location or phone number…
            </Typography>
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              borderColor: "#E5E7EB",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <FilterListIcon sx={{ fontSize: 14, color: "#6B7280" }} />
            <Typography variant="caption" sx={{ color: "#111827" }}>
              All Status
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 14, color: "#6B7280" }} />
          </Paper>
          <Paper
            variant="outlined"
            sx={{
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              borderColor: "#E5E7EB",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Typography variant="caption" sx={{ color: "#111827" }}>
              25 per page
            </Typography>
            <ExpandMoreIcon sx={{ fontSize: 14, color: "#6B7280" }} />
          </Paper>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.25, borderColor: "#E5E7EB" }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.9rem" }}>
              Downtown Location
            </Typography>
            <Box
              sx={{
                px: 0.75,
                py: 0.125,
                borderRadius: 999,
                bgcolor: "#DCFCE7",
                color: "#166534",
                fontSize: "0.7rem",
                fontWeight: 600,
              }}
            >
              Active
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: "#6B7280", mb: 2 }}>
            <PhoneIcon sx={{ fontSize: 12 }} />
            <Typography variant="caption">(555) 123-4567</Typography>
            <Typography variant="caption">· Professional</Typography>
            <Typography variant="caption">· Michael</Typography>
            <Typography variant="caption">· 2 languages</Typography>
          </Stack>
          <Grid container spacing={1.5}>
            {rowCols.map((c) => (
              <Grid item xs={6} sm={2.4} key={c.label}>
                <Typography variant="caption" sx={{ color: "#6B7280", display: "block" }}>
                  {c.label}
                </Typography>
                <Typography sx={{ fontWeight: 700, color: "#111827", fontSize: "0.95rem" }}>
                  {c.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Paper>
    </Box>
  );
}

// Screenshot preview of the actual Cinch wizard (frontend-st voice-agent-wizard).
// Shown in the "Set Up Your AI Call Assistant in Minutes" section — the active
// step's screenshot swaps in based on which accordion row the visitor clicks.
// Shots were taken against a dev company. If the wizard UI changes meaningfully,
// re-capture the PNGs under src/assets/va-wizard-step{1..5}.png.
// Raw PNG dimensions captured from the running app at 1280x900.
// The content panel we want sits at x=360..1280 (width 920) and y=64..900
// (height 836); the rest is sidebar + breadcrumb/working-as chrome that
// doesn't belong on a marketing page. We clip to that area with CSS so the
// screenshot responsively fits whatever width the column renders at.
const WIZARD_SHOT_FULL_W = 1280;
const WIZARD_SHOT_FULL_H = 900;
const WIZARD_SHOT_CROP_X = 360;
const WIZARD_SHOT_CROP_Y = 64;
const WIZARD_SHOT_CROP_W = WIZARD_SHOT_FULL_W - WIZARD_SHOT_CROP_X; // 920
const WIZARD_SHOT_CROP_H = WIZARD_SHOT_FULL_H - WIZARD_SHOT_CROP_Y; // 836

function WizardStepPreview({ src, label }) {
  const aspectPaddingPct = (WIZARD_SHOT_CROP_H / WIZARD_SHOT_CROP_W) * 100;
  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: { xs: 1, md: 1.5 },
        borderRadius: 2,
        borderColor: "#E4E7EC",
        bgcolor: "#FAFAFA",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          pt: `${aspectPaddingPct}%`,
          overflow: "hidden",
          borderRadius: 1.5,
          bgcolor: "#F5F5F5",
        }}
      >
        <Box
          component="img"
          src={src}
          alt={`Wizard preview — ${label}`}
          sx={{
            position: "absolute",
            top: `${(-WIZARD_SHOT_CROP_Y / WIZARD_SHOT_CROP_H) * 100}%`,
            left: `${(-WIZARD_SHOT_CROP_X / WIZARD_SHOT_CROP_W) * 100}%`,
            width: `${(WIZARD_SHOT_FULL_W / WIZARD_SHOT_CROP_W) * 100}%`,
            height: "auto",
            display: "block",
          }}
        />
      </Box>
    </Paper>
  );
}

// Simple decorative "Call Topics" bar visual for the dark-navy card.
// Not driven by real data — it's just an at-a-glance illustration of what
// the agent's call-topics summary looks like.
function CallTopicsChart() {
  const rows = [
    { label: "Schedule", pct: 47 },
    { label: "Other", pct: 36 },
    { label: "Services", pct: 5 },
    { label: "Offers", pct: 6 },
  ];
  return (
    <Box sx={{ bgcolor: "#FFFFFF", color: "#111827", borderRadius: 1.5, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Call Topics</Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#6B7280" }}>Based on 10 calls</Typography>
      </Stack>
      <Stack spacing={1.25}>
        {rows.map((r) => (
          <Box key={r.label}>
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontSize: "0.8rem" }}>{r.label}</Typography>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#0875BF" }}>
                {r.pct}%
              </Typography>
            </Stack>
            <Box sx={{ height: 4, bgcolor: "#EFF6FF", borderRadius: 2, mt: 0.5, overflow: "hidden" }}>
              <Box sx={{ width: `${r.pct}%`, height: "100%", bgcolor: "#0875BF" }} />
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// Simple decorative line-chart illustration for the dark-navy right card.
function UsageTrendChart() {
  return (
    <Box sx={{ bgcolor: "#FFFFFF", color: "#111827", borderRadius: 1.5, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Monthly Usage Trend</Typography>
        <Box sx={{ bgcolor: "#D6E7F3", color: "#0875BF", borderRadius: 999, px: 1.25, py: 0.25 }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700 }}>Unlimited Plan</Typography>
        </Box>
      </Stack>
      <Box sx={{ position: "relative", height: 110 }}>
        <svg viewBox="0 0 300 110" width="100%" height="100%">
          {[0, 1, 2, 3].map((i) => (
            <line
              key={i}
              x1="0"
              x2="300"
              y1={25 + i * 25}
              y2={25 + i * 25}
              stroke="#F3F4F6"
              strokeWidth="1"
            />
          ))}
          <path
            d="M10,80 L75,55 L150,40 L225,45 L290,40"
            fill="none"
            stroke="#0875BF"
            strokeWidth="2.5"
          />
          <path
            d="M10,80 L75,55 L150,40 L225,45 L290,40 L290,110 L10,110 Z"
            fill="#0875BF"
            fillOpacity="0.08"
          />
          {["300", "400", "500"].map((lbl, idx) => (
            <text
              key={lbl}
              x="2"
              y={85 - idx * 25}
              fill="#9CA3AF"
              fontSize="9"
              fontFamily="Montserrat, sans-serif"
            >
              {lbl}
            </text>
          ))}
        </svg>
      </Box>
    </Box>
  );
}
